import {
  Component, forwardRef, Input, Output, EventEmitter,
  ViewChild, ElementRef, HostListener
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

/**
 * Atomic date-picker.
 * Shows the selected date as readable text + a calendar icon button.
 * The native <input type="date"> is hidden and triggered on explicit click only,
 * so it never auto-opens on focus or interferes with other fields.
 *
 * Selector: <ui-date-picker>
 * Supports [(ngModel)] via ControlValueAccessor.
 * Value format: 'YYYY-MM-DD'
 */
@Component({
  selector: 'ui-date-picker',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
  template: `
    <div class="dp-wrap" (click)="openPicker()">
      <span class="dp-label">{{ displayValue }}</span>
      <mat-icon class="dp-icon">calendar_today</mat-icon>
      <!-- Hidden native input — opened programmatically only -->
      <input
        #nativeInput
        type="date"
        class="dp-hidden-input"
        [value]="innerValue"
        [max]="max"
        [min]="min"
        (change)="onNativeChange($any($event.target).value)"
        (blur)="onTouched()"
        tabindex="-1"
        aria-hidden="true"
      />
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; }

    .dp-wrap {
      position: relative;
      display: flex;
      align-items: center;
      height: 38px;
      border-radius: 10px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      padding: 0 10px;
      box-sizing: border-box;
      cursor: pointer;
      background: #fff;
      gap: 6px;
      transition: border-color 0.15s;
      overflow: hidden;
    }

    .dp-wrap:hover {
      border-color: #f26600;
    }

    .dp-label {
      flex: 1;
      font-size: 13px;
      color: #374151;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;
    }

    .dp-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
      color: #94a3b8;
      flex-shrink: 0;
      transition: color 0.15s;
    }

    .dp-wrap:hover .dp-icon {
      color: #f26600;
    }

    /* Hidden native input — covers the wrapper exactly so the browser
       anchors its picker popup relative to this element */
    .dp-hidden-input {
      position: absolute;
      inset: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      pointer-events: none;  /* clicks go to the wrapper, we open manually */
    }
  `]
})
export class DatePickerComponent implements ControlValueAccessor {
  @ViewChild('nativeInput') nativeInputRef!: ElementRef<HTMLInputElement>;

  @Input() max: string = '';
  @Input() min: string = '';

  innerValue: string = '';
  private onChange = (_: string) => {};
  onTouched = () => {};

  get displayValue(): string {
    if (!this.innerValue) return 'Seleccionar fecha';
    const [year, month, day] = this.innerValue.split('-').map(Number);
    if (!year) return this.innerValue;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  openPicker(): void {
    const input = this.nativeInputRef?.nativeElement;
    if (!input) return;
    input.focus();
    input.showPicker?.();
  }

  onNativeChange(value: string): void {
    this.innerValue = value;
    this.onChange(value);
  }

  writeValue(value: string): void {
    this.innerValue = value ?? '';
  }

  registerOnChange(fn: (_: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {}
}
